/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Grid, IconButton, lighten, Stack, Toolbar as MuiToolbar, Tooltip, Typography } from '@mui/material'
import Search from '@mui/icons-material/Search'
import { ReactNode, useState } from 'react'
import { UseFiltersInstanceProps } from 'react-table'
import { EnhancedHeader } from '../../../types'
import { SimpleMap } from '@vendorflow/types'
import GlobalSearchFilter from './GlobalSearchFilter'
import ViewColumnTool from './ViewColumnTool'
import FilterTool from '../FilterTool'

export const MobilePopoverStyling = css`
  @media screen and (max-width: 480px) {
    width: calc(100vw - 2rem);
  }
`

interface Props<D extends object> {
  title?: string
  allColumns: EnhancedHeader<D>[]
  columnNameById: SimpleMap<string>
  enableFilters?: boolean
  enableGlobalSearch?: boolean
  enableHideColumns?: boolean
  hasRowsSelected: boolean
  numRowsSelected: number
  globalFilter: string
  renderActions?: () => ReactNode
  renderRowSelectActions?: () => ReactNode
  setAllFilters: UseFiltersInstanceProps<D>['setAllFilters']
  setGlobalFilter: (filterValue: any) => void | Promise<void>
}

export default function Toolbar<D extends object>({
  title,
  allColumns,
  columnNameById,
  enableFilters,
  enableGlobalSearch,
  enableHideColumns,
  hasRowsSelected,
  numRowsSelected,
  globalFilter,
  renderActions,
  renderRowSelectActions,
  setAllFilters,
  setGlobalFilter,
}: Props<D>) {
  const [showSearch, setShowSearch] = useState(false)

  return (
    <MuiToolbar
      sx={hasRowsSelected ? { backgroundColor: (theme) => lighten(theme.palette.primary.light, 0.85) } : undefined}
    >
      <Grid container spacing={2}>
        <Grid item md={6} sm={6} xs={12}>
          <Stack direction="row">
            {enableGlobalSearch && showSearch ? (
              <GlobalSearchFilter
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                setShowSearch={setShowSearch}
              />
            ) : hasRowsSelected ? (
              <Typography
                css={css`
                  font-size: 1.125rem;
                  margin-top: 4px;
                `}
              >
                {numRowsSelected} selected
              </Typography>
            ) : (
              <Typography
                css={css`
                  font-size: 1.125rem;
                  margin-top: 6px;
                `}
              >
                {title || ''}
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid item md={6} sm={6} xs={12}>
          <Stack direction="row" justifyContent="flex-end" width="100%">
            {enableGlobalSearch && (
              <Tooltip title="Search">
                <IconButton
                  onClick={() => {
                    if (showSearch) {
                      setGlobalFilter(null)
                    }
                    setShowSearch(!showSearch)
                  }}
                >
                  <Search />
                </IconButton>
              </Tooltip>
            )}
            {enableHideColumns && <ViewColumnTool allColumns={allColumns} columnNameById={columnNameById} />}
            {enableFilters && (
              <FilterTool allColumns={allColumns} columnNameById={columnNameById} setAllFilters={setAllFilters} />
            )}
            {hasRowsSelected ? renderRowSelectActions && renderRowSelectActions() : renderActions && renderActions()}
          </Stack>
        </Grid>
      </Grid>
    </MuiToolbar>
  )
}
